import { CSSObject } from '@emotion/react';
import { useIsTouchDevice } from '../../../../hooks/useIsTouchDevice';
import { useTheme } from '../../../../theme';
import { useViewport } from '../../../../hooks/useViewport';

export type UsePhoneInputStylesProps = {
  hasError?: boolean;
  isDisabled?: boolean;
};
export const usePhoneInputStyles = ({
  hasError,
  isDisabled,
}: UsePhoneInputStylesProps): CSSObject => {
  const theme = useTheme();
  const isTouchDevice = useIsTouchDevice();
  const { isSmallViewport } = useViewport();

  return {
    border: `1px solid ${
      hasError ? theme.border.interactive.negative : theme.border.primary
    }`,
    borderRadius: theme.shape.radius8,
    boxShadow: theme.typography.inputShadow,
    minHeight: 40,

    '&:has(input:disabled)': {
      backgroundColor: theme.input.background.disabled,
      borderColor: theme.border.primary,
    },

    '&:has(input:focus:not(:disabled))': {
      borderColor: hasError
        ? theme.input.border.error
        : theme.input.border.active,
      outline: `4px solid ${
        hasError ? theme.input.border.errorFocused : theme.input.border.focused
      }`,
    },

    '&:has(input:hover:not(:disabled))': {
      borderColor: hasError
        ? theme.input.border.error
        : theme.input.border.active,
    },

    '.intl-tel-input.allow-dropdown': {
      display: 'flex',
      flexGrow: 1,

      '&.expanded': {
        '.selected-flag, .selected-flag:hover': {
          backgroundColor: theme.filter.active,
        },
      },

      '.country-list': {
        ...theme.typography.bodyS,
        fontFamily: theme.typography.fontFamily,
        borderRadius: theme.shape.radius8,
        border: `1px solid ${theme.menu.background.border}`,
        background: theme.menu.background.default,
        boxShadow: theme.shadows[2],
        backdropFilter: theme.backdropFilter.xlarge,
        maxHeight: 320,
        marginTop: 1,
        padding: theme.spacing(1, '1px'),
        zIndex: theme.zIndex.popover,
        width: hasError ? 'calc(100% + 30px)' : 'calc(100% + 2px)',
        marginLeft: -2,

        '.divider': {
          height: 1,
          border: `1px solid ${theme.popover.border}`,
          margin: `${theme.spacing(1)} 0`,
          paddingBottom: 0,
        },

        '.country-name': {
          marginRight: theme.spacing(2),
          color: theme.text.generic.primary,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },

        '.country': {
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr max-content',
          alignItems: 'center',
          minHeight: 40,
          padding: theme.spacing('10px', 3),

          '&.highlight': {
            background: theme.menu.background.selected,
          },
        },

        '.dial-code': {
          color: theme.text.generic.tertiary,
          ...theme.typography.bodyS,
        },
      },

      '.selected-flag': {
        borderRadius: theme.shape.radius4,
        outlineColor: theme.border.interactive.default,
        background: theme.filter.default,
        width: 50,
        height: 32,
        margin: 2,

        ...(isDisabled && {
          cursor: 'auto',
          background: 'none !important',
        }),

        /**
         * When you click on arrow it does nothing as it is not handled internally in the intl-tel-input.
         * We need to bubble up the event so parent would handle it properly.
         */
        '.arrow': {
          pointerEvents: 'none',
        },

        '&:hover': {
          background: theme.filter.hover,
        },

        '&:focus': {
          outline: !isDisabled
            ? `2.5px solid ${theme.button.focused.default}`
            : undefined,
        },
      },

      '.phone-input': {
        ...(isTouchDevice && isSmallViewport
          ? theme.typography.bodyM
          : theme.typography.bodyS),
        fontFamily: 'inherit',
        color: isDisabled
          ? theme.text.interactive.disabled
          : theme.text.generic.primary,
        borderRadius: theme.shape.radius8,
        backgroundColor: 'transparent',
        caretColor: hasError
          ? theme.palette.alert.negative[100]
          : theme.blinkingCursor.cursor,
        flexGrow: 1,
        paddingLeft: 65,
        border: 'none',
        outline: 'none',

        '&::placeholder': {
          opacity: 1,
          color: theme.text.interactive.disabled,
        },
      },

      '.flag-container': {
        '.arrow': {
          margin: theme.spacing('2px', 0, '1px', '2px'),
        },

        '.arrow:after': {
          // Icon.ChevronDown
          content:
            'url(\'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M3.55505 5.50358C3.8292 5.25781 4.25069 5.28083 4.49645 5.55498L7.78935 9.22826C7.91492 9.36834 8.08517 9.36834 8.21075 9.22826L11.5036 5.55498C11.7494 5.28083 12.1709 5.25781 12.445 5.50358C12.7192 5.74935 12.7422 6.17083 12.4965 6.44499L9.20356 10.1183C8.54809 10.8494 7.45201 10.8495 6.79654 10.1183L3.50364 6.44499C3.25787 6.17083 3.28089 5.74935 3.55505 5.50358Z" fill="%23232323"/></svg>\')',
          display: 'block',
          transition: `transform ${theme.transitions.duration.short}ms`,
          transformOrigin: 'center',
        },

        '.arrow.up': {
          marginTop: -2,
          '&:after': {
            transform: 'rotate(180deg)',
          },
        },
      },
    },
  };
};
